import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Tree} from 'primeng/tree';
import {MenuItem, TreeNode} from 'primeng/api';
import {get} from 'lodash';
import {select, Store} from "@ngrx/store";
import {State} from '@ispm-client/modules/bpmn-modeler/store/state';
import {selectExplorerViewCommand} from '@ispm-client/modules/bpmn-modeler/store/selectors/explorer-view-command.selector';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {NodeType} from "@ispm/shared/bpmn-io/node-type";
import {StringUtils} from '@ispm/shared/utils/String.utils';

@Component({
  selector: 'ispm-json-tree-viewer',
  templateUrl: './json-tree-viewer.component.html',
  styleUrls: ['./json-tree-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JsonTreeViewerComponent implements OnInit, OnDestroy {

  @ViewChild('expandingTree', {static: false}) public expandingTree: Tree;
  @ViewChild('cm') public contextMenu;

  @Input() filterMode: string = 'strict'; // possible values: 'strict' , 'lenient'
  @Input() selectionMode: string = 'single';
  @Input() placeholderText: string = 'Search node';
  @Input() useFilter: boolean = false;
  @Input() draggableNodes: boolean = false;
  @Input() droppableNodes: boolean = false;
  @Input() showButtons: boolean = false;
  @Input() showButtonsAbove: boolean = true;
  @Input() showButtonsBelow: boolean = false;
  @Input() maxHeight: string = '25rem';
  @Input() isExpanded: boolean = false;
  @Input() nodesStateMap: Map<string, boolean> = undefined;

  @Input()
  set treeNodes(values: any) {
    this._nodes = values.collapsedData;
    this._nodesCollapsed = values.collapsedData;
    this._nodesExpanded = values.expandedData;
  }

  get treeNodes() {
    return this.isExpanded ? this._nodesExpanded : this._nodesCollapsed;
  }

  @Input()
  set contextMenuItems(values: MenuItem[]) {
    if (!!values) {
      values.forEach(item => {
        const menuItem = {
          label: get(item, 'label', ''),
          icon: get(item, 'icon', 'pi'),
          command: (event) => this.doSomething(get(item, 'command', '').toString(), this.selectedNode)
        };
        this._contextMenuItems.push(menuItem);
      });
    } else {
      this._contextMenuItems = undefined;
    }
  }

  get contextMenuItems() {
    return this._contextMenuItems;
  }

  @Output() executeCommand: EventEmitter<any> = new EventEmitter();
  @Output() expandedNodeState: EventEmitter<any> = new EventEmitter();

  protected destroyer$: Subject<null> = new Subject();
  private _nodes: TreeNode[];
  private _nodesExpanded: TreeNode[];
  private _nodesCollapsed: TreeNode[];
  public selectedNode: TreeNode;
  public loading: boolean = false;
  public _contextMenuItems: MenuItem[] = [];
  public searchText: string;
  public filterSearch: Subject<string> = new Subject<string>();
  private lastSelectedNode: TreeNode = undefined;
  public searchIsRunning: boolean = false;
  public buttonTitle: string = '';

  constructor(private store$: Store<State>,
              private changeDetector: ChangeDetectorRef) {
    this.filterSearch
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value: string) => {
        this.onSearchExecute(value);
      });
  }

  public ngOnInit(): void {

    this.buttonTitle = this.getTitle();

    this.store$.pipe(select(selectExplorerViewCommand), takeUntil(this.destroyer$))
      .subscribe((selectedNodeData) => {
        this.setSelectionProperties(this.lastSelectedNode, false);
        if (!!selectedNodeData) {
          if (!!get(selectedNodeData, 'nodeId', undefined)) {
            const selectedNodeType = get(selectedNodeData, 'type', undefined);
            switch (selectedNodeType) {
              case NodeType.neighborhood_head:
                this.selectedNode = this.findNode(get(selectedNodeData, 'parent', undefined));
                break;
              default:
                this.selectedNode = this.findNode(get(selectedNodeData, 'nodeId', undefined));
            }
            this.setSelectionProperties(this.selectedNode, true);
          } else {
            this.selectedNode = undefined;
          }

          this.lastSelectedNode = this.selectedNode;

        }
        this.changeDetector.detectChanges();
        // don't move this string, search is executed after tree update in DOM
        this.treeScrollToSelectedItem(this.expandingTree, 'p-treenode-content p-treenode-selectable p-highlight');
      });

    // restore tree state when we open explorer view again
    this.nodesStateMap.forEach((value, key) => {
      const node = this.findNode(key);
      if (!!node) {
        node.expanded = value;
      }
    });
    this.changeDetector.detectChanges();

  }

  public expandAll() {
    if (!this.searchIsRunning) {
      this.isExpanded = !this.isExpanded;
      this.buttonTitle = this.getTitle();
      this.searchText = '';
      this.executeFilter(this.searchText);

      this.executeCommand.emit({command: 'ChangeExpand', state: this.isExpanded});
      this._nodes = this.isExpanded ? this._nodesExpanded : this._nodesCollapsed;

      this._nodes.forEach((node) => {
        this.expandRecursive(node, this.isExpanded);
      });
    }
  }

  private expandRecursive(node: TreeNode, isExpand: boolean) {
    if (node.children ) {
      node.expanded = node.leaf ? false: isExpand;
      node.label = node.label
        .split(StringUtils.OPEN_SEC).join('')
        .split(StringUtils.CLOSE_SEC).join('');
      node.children.forEach((childNode) => {
        this.expandRecursive(childNode, isExpand);
      });
    }
  }

  public nodeExpand(event) {
    this.executeCommand.emit({command: 'NodeExpanded', nodeId: event.node.data.id, expanded: event.node.expanded});

  }

  public nodeCollapse(event) {
    if (event.node && event.node.parent === undefined && event.node.data.type === NodeType.head) {
      this.isExpanded = false;
      this.buttonTitle = this.getTitle();
    }
    this.executeCommand.emit({command: 'NodeExpanded', nodeId: event.node.data.id, expanded: event.node.expanded});
  }

  public nodeSelect(event) {
    event.node = this.selectedNode;
    this.setSelectionProperties(this.lastSelectedNode, false);
    this.setSelectionProperties(this.selectedNode, true);
    this.lastSelectedNode = this.selectedNode;
    this.changeDetector.detectChanges();
    return this.executeCommand.emit({command: 'SelectedNode', selectedNode: this.selectedNode});
  }

  public setSelectionProperties(node: TreeNode<any>, select: boolean) {
    if (!!node) {
      if (node.icon.length > 0) {
        node.icon = select && !node.icon.includes('_white') ? node.icon.concat('_white') : node.icon;
        node.icon = !select && node.icon.includes('_white') ? node.icon.replace('_white', '') : node.icon;
      }
    }
  }

  public nodeUnselect(event) {
  }

  public onNodeRightButtonClick($event) {
    const cmdiv = this.contextMenu.el.nativeElement.querySelectorAll('div')[0];
    const nodeY = $event.originalEvent.layerY;
    let style = cmdiv.getAttribute('style');
    style = style.concat('top:' + nodeY + 'px');
    cmdiv.setAttribute('style', style);
  }

  private doSomething(command: string, selectedNode: TreeNode) {
    return this.executeCommand.emit({command: command, selectedNode: selectedNode});
  }

  public getMaxHeight() {
    return this.maxHeight.toString() + 'rem';
  }

  private getTitle() {
    return this.isExpanded ? 'Collapse all' : 'Expand all';
  }

  public ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  public onSearchChange($event: any) {
    this.filterSearch.next($event);
  }

  public onSearchExecute(searchStr: string) {
    this.executeFilter(searchStr);
  }

  public onSearchClear(searchStr: string) {
    this.executeFilter(searchStr);
  }

  private executeFilter(searchStr: string) {
    this.searchIsRunning = this.isFilterNotEmpty(searchStr);

    if (!this.searchIsRunning && this.isExpanded) {
      this._nodes.forEach((node) => {
        this.expandRecursive(node, this.isExpanded);
      });
    }
    // Save search text
    this.searchText = searchStr;

    // If search text is not empty, then behavior for expanded or collapsed tree should be the same
    if (this.searchIsRunning) {
      this._nodes.forEach((node) => {
        this.collapseRecursive(node, this.searchText.toLowerCase());
      });
    }
    this.expandingTree._filter(searchStr);

    const chunks: string[] = StringUtils.getChunks(searchStr);
    setTimeout(() =>
        this.walkByTree(chunks),
      200);
  }

  isFilterNotEmpty(searchStr:string): boolean {
    return !!searchStr && searchStr.length >0;
  }

  private markNodes(chunks: string[], element: TreeNode) {
    if (!!element) {
      element.label = this.markSearchText(element.label, chunks);
      element.children.forEach(child => {
        this.markNodes(chunks, child);
      });
    }
  }

  private findNode(data: string) {
    let foundNode = this._nodes[0];
    return this.searchTree(foundNode, data);
  }

  private searchTree(element: TreeNode, data: string) {
    if (element.data.id === data) {
      return element;
    } else if (element.children != null) {
      let result = null, nd = null;
      element.children.forEach(child => {
        nd = this.searchTree(child, data);
        if (nd != null) {
          result = nd;
        }
      });
      return result;
    }

    return null;
  }

  public onFilter($event: any) {
  }

  private markSearchText(value: string, searchChunks: string[]): string {
    try {
      if (!!value) {
        let outValue = value
          .split(StringUtils.OPEN_MARK).join('')
          .split(StringUtils.CLOSE_MARK).join('')
          .split(StringUtils.OPEN_SEC).join('')
          .split(StringUtils.CLOSE_SEC).join('');
        ;
        searchChunks.forEach(chunk => {
          const regex = new RegExp('(' + chunk + ')', 'ig');
          outValue = outValue.replace(regex, StringUtils.REPLACE_EXPRESSION);
        });
        return outValue;
      }
    } catch (e) {
    }
    return value;
  }

  private walkByTree(searchChunks: string[]) {
    this.walkDOM(this.expandingTree.el.nativeElement, searchChunks, function (node, chunks) {
      if (node && node.tagName && node.tagName.toLowerCase() === 'span' && node.className === 'p-treenode-label') {
        let value = node.innerText;
        let outValue = value
          .split(StringUtils.OPEN_MARK).join('')
          .split(StringUtils.CLOSE_MARK).join('')
          .split(StringUtils.OPEN_SEC).join('')
          .split(StringUtils.CLOSE_SEC).join('');
        ;
        searchChunks.forEach(chunk => {
          const regex = new RegExp('(' + StringUtils.escapeRegExp(chunk) + ')', 'ig');
          outValue = outValue.replace(regex, StringUtils.REPLACE_EXPRESSION);
        });
        node.innerText = outValue;
        const val = node.innerHTML.split(StringUtils.OPEN_SEC).join(StringUtils.OPEN_MARK)
          .split(StringUtils.CLOSE_SEC).join(StringUtils.CLOSE_MARK);
        node.innerHTML = val;
      }
    });
  }

  private cleanTree() {
    let val = this.expandingTree.el.nativeElement.innerHTML;
    this.expandingTree.el.nativeElement.innerHTML = val.split(StringUtils.OPEN_MARK).join('').split(StringUtils.CLOSE_MARK).join('');
  }

  private walkDOM(node, searchChunks, func): void {
    func(node, searchChunks);
    node = node.firstChild;
    while (node) {
      this.walkDOM(node, searchChunks, func);
      node = node.nextSibling;
    }
  }

  // selection is the TreeNode you want to scroll into view
  private treeScrollToSelectedItem(tree: Tree, className: string): void {
    const treeElements = document.getElementsByTagName('p-tree');
    if (treeElements && treeElements.length > 0) {
      const nodes = treeElements[0].getElementsByClassName(className);
      if (nodes.length > 0) {
        nodes[0].scrollIntoView();
      }
    }
  }

  private getNodeState(id: string): boolean {
    return !!this.nodesStateMap && this.nodesStateMap.has(id) ? this.nodesStateMap.get(id) : this.isExpanded;
  }

  private collapseRecursive(node: TreeNode, chunk: string) {
    if (node.children && this.isExpanded) {
      node.expanded = !node.label.toLowerCase().includes(chunk);
      if(node.expanded) {
        node.children.forEach((childNode) => {
          this.collapseRecursive(childNode, chunk);
        });
      }
    }
  }
}
